import {
  calculatePosition, calculationDraftSchema, calculationPositionSchema,
  type CalculationDraft, type CalculationPosition,
} from "@shared/calculation-template";
import type { LVPosition, LVSektion } from "@/components/project/leistungsverzeichnis-kalkulator";
import type { ProjectCalculations, ProjectSectionTitles } from "./project-position-calculation";

const unitKey = (value: string) => value.trim().toLowerCase().replace(/²/g, "2").replace(/³/g, "3");
const usableUnit = (value: string) => !!value.trim() && !/^ohne\s*einheit$/i.test(value.trim());

export interface OfferTemplateItem {
  id: string; description: string; unit: string; unitPrice: number; quantity: number; variant?: boolean;
}
export interface OfferTemplatePhoto {
  positions: Array<{ posId?: string; name: string; unit: string; ep: number; qty: number; variant?: boolean }>;
}

/** Use exactly the offer's rendered rows, including manual and photo services,
 * not just the restricted editable LV. Repeated equal photo services become one
 * reusable position; differing prices/units remain distinct and visible. */
export function assembleOfferTemplateSections({
  items, photos = [], lvPositions = [], calculations = {}, sectionTitles = {}, groups,
}: {
  items: OfferTemplateItem[]; photos?: OfferTemplatePhoto[]; lvPositions?: LVPosition[];
  calculations?: ProjectCalculations; sectionTitles?: ProjectSectionTitles;
  groups: Array<{ id: string; title: string; positionIds: string[] }>;
}): LVSektion[] {
  const sections = groups.map((group) => ({
    id: group.id, titel: sectionTitles[group.id] || group.title, icon: "", positionen: [] as LVPosition[],
  }));
  const seen = new Set<string>();
  const add = (id: string, sourceId: string, name: string, unit: string, ep: number, qty: number, variant?: boolean) => {
    if (variant) return;
    const lv = lvPositions.find((position) => position.id === sourceId);
    const calculation = calculations[sourceId] ?? lv?.calculation;
    const section = sections.find((section) => section.id === calculation?.section) ??
      sections.find((section) => groups.find((group) => group.id === section.id)?.positionIds.includes(sourceId)) ??
      sections.find((section) => section.id === "s7");
    if (!section) throw new Error("Die Leistungsgruppe für eigene Angebotspositionen fehlt.");
    const key = JSON.stringify([sourceId, section.id, name, unitKey(unit), ep]);
    if (seen.has(key)) return;
    seen.add(key);
    section.positionen.push({
      id: sourceId || id, name, unit, ep, qty, aktiv: true,
      calculation: calculation && unitKey(calculation.unit) === unitKey(unit) ? calculation : undefined,
    });
  };
  items.forEach((item) => {
    const sourceId = item.id.startsWith("lv-") ? item.id.slice(3) :
      item.id.startsWith("pp-agg-") ? item.id.slice(7) : item.id;
    add(item.id, sourceId, item.description, item.unit, item.unitPrice, item.quantity, item.variant);
  });
  photos.forEach((photo, photoIndex) => photo.positions.forEach((position, index) => {
    const id = position.posId || `photo-service-${photoIndex}-${index}`;
    add(`photo-template-${photoIndex}-${index}`, id, position.name, position.unit, position.ep, position.qty, position.variant);
  }));
  return sections;
}

/** Build a deliberate independent copy from the CURRENT editor, not stale saved
 * project data or a live source template. Jobsite quantities are not reusable. */
export function buildCalculationTemplateFromOffer(name: string, sections: LVSektion[]): CalculationDraft {
  const positions: CalculationPosition[] = [];
  const sectionTitles: Record<string, string> = {};
  const usedCodes = new Set<string>();
  const missing: string[] = [], fixed: string[] = [], renamed: string[] = [], units: string[] = [];
  for (const section of sections) {
    for (const row of section.positionen.filter((row) => row.aktiv && !row.variant)) {
      if (!Number.isFinite(row.ep) || Math.abs(row.ep) > 1e9) throw new Error(`Ungültiger Angebotspreis bei „${row.name}“.`);
      const source = row.calculation;
      const oldCode = source?.code ??
        row.id.match(/^custom-k7-[0-9a-f-]{36}-(.+)$/i)?.[1] ??
        `ANG-${String(positions.length + 1).padStart(3, "0")}`;
      let code = oldCode.trim().slice(0, 60);
      if (!code) code = `ANG-${String(positions.length + 1).padStart(3, "0")}`;
      const originalCode = code;
      for (let index = 2; usedCodes.has(code); index++) {
        const suffix = `-${index}`;
        code = originalCode.slice(0, 60 - suffix.length) + suffix;
      }
      if (code !== originalCode) renamed.push(`${originalCode} → ${code}`);
      usedCodes.add(code);
      let rowName = row.name.trim();
      if (rowName.startsWith(`${oldCode} `)) rowName = rowName.slice(oldCode.length).trim();
      if (!rowName) throw new Error(`Bitte die Bezeichnung der Position ${code} ergänzen.`);
      const unit = row.unit.trim() || "ohne Einheit";
      if (!usableUnit(unit)) units.push(code);
      let position: CalculationPosition;
      if (source) {
        if (unitKey(source.unit) !== unitKey(unit)) {
          throw new Error(`Bei „${row.name}“ stimmt die Einheit des Angebots nicht mit der Kalkulation überein. Bitte zuerst im Projekt prüfen.`);
        }
        const parsed = calculationPositionSchema.safeParse(source);
        if (!parsed.success) throw new Error(`Die Kalkulationsdaten bei „${row.name}“ sind ungültig. Bitte zuerst im Projekt prüfen.`);
        const { libraryPositionId, ...snapshot } = structuredClone(parsed.data);
        position = { ...snapshot, code, name: rowName, unit, section: section.id as CalculationPosition["section"] };
        if (Math.abs(calculatePosition(position).price - row.ep) > 1e-9) {
          position.priceOverride = row.ep;
        }
        if (position.priceOverride !== undefined) fixed.push(code);
        if (position.k7Price === null && !position.manualCalculation) missing.push(code);
      } else {
        missing.push(code);
        position = {
          code, name: rowName, unit, section: section.id as CalculationPosition["section"],
          lvPrice: row.ep, k7Price: null, originalLaborCost: 0, originalOtherCost: 0,
          originalLineLabor: 0, originalLineOther: 0, laborMarkup: 0, otherMarkup: 0, lines: [],
          warnings: ["Aus Angebot übernommen: Nur der Einheitspreis ist bekannt; keine gespeicherten K7-Kalkulationsansätze vorhanden."],
        };
      }
      positions.push(position);
      sectionTitles[section.id] = section.titel.trim();
    }
  }
  if (!positions.length) throw new Error("Das Angebot enthält keine aktiven Positionen für eine Vorlage.");
  const warnings: string[] = [];
  const examples = (values: string[]) => values.slice(0, 8).join(", ") + (values.length > 8 ? ", …" : "");
  if (missing.length) warnings.push(`${missing.length} Position(en) ohne gespeicherte Kalkulationsansätze: ${examples(missing)}. Nur Einheitspreise werden übernommen; historische K7-Werte werden nicht erfunden oder aus aktuellen Vorlagen nachgeladen.`);
  if (fixed.length) warnings.push(`${fixed.length} abweichende Angebotspreis(e) werden ausdrücklich fixiert: ${examples(fixed)}. Die vorhandenen Ansätze bleiben erhalten; für neue kalkulierte Preise muss die Preisfixierung aufgehoben werden.`);
  if (renamed.length) warnings.push(`Doppelte Positionsnummern wurden für die unabhängige Vorlage eindeutig ergänzt: ${examples(renamed)}.`);
  if (units.length) warnings.push(`Bei ${units.length} Position(en) fehlt eine verwendbare Einheit: ${examples(units)}. Bitte vor Wiederverwendung in der Vorlage ergänzen.`);
  const result = calculationDraftSchema.safeParse({ name, positions, warnings, sectionTitles });
  if (!result.success) throw new Error(result.error.issues[0]?.message ?? "Die Angebotsdaten sind als Vorlage nicht gültig.");
  return result.data;
}
